import React, { useCallback, useContext, useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  InputAdornment,
  IconButton,
  Card,
  CardContent,
  Alert,
} from '@mui/material';
import { Visibility as EyeIcon } from '@mui/icons-material';
import { observer } from 'mobx-react-lite';
import { change, changePassword } from '../http/userAPI';
import { Context } from '..';
import { getAccess } from '../http/accessAPI';

const Profile = observer(() => {
  const { user } = useContext(Context);

  const [internalName, setInternalName] = useState(user.user.internalName || '');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertSeverity, setAlertSeverity] = useState('success');
  const [timerId, setTimerId] = useState(null);

  const handleCopyToClipboard = () => {
    navigator.clipboard.writeText(user?.id || '');
    handleAlert('ID скопирован', 'success');
  };

  const handleAlert = useCallback(
    (message, severity) => {
      if (timerId) {
        clearTimeout(timerId);
      }

      setAlertMessage(message);
      setAlertSeverity(severity);
      setAlertOpen(true);

      const newTimerId = setTimeout(() => setAlertOpen(false), 3000);
      setTimerId(newTimerId);
    },
    [timerId]
  );

  const handleAlertClose = () => {
    setAlertOpen(false);
    if (timerId) {
      clearTimeout(timerId);
    }
  };

  const handleChangePassword = useCallback(async () => {
    if (!currentPassword || !newPassword) {
      handleAlert('Пожалуйста, заполните все поля.', 'error');
      return;
    }

    try {
      const status = await changePassword(currentPassword, newPassword);
      if (status === 200 || status === true) {
        setCurrentPassword('');
        setNewPassword('');
        handleAlert('Пароль успешно изменен!', 'success');
      }
    } catch (error) {
      console.error("Ошибка при обновлении данных:", error);
      handleAlert(`Не верные данные для смены пароля `, "error");
    }
  }, [currentPassword, handleAlert, newPassword]);

  const handleSaveChanges = useCallback(async () => {
    if (!user.user.name || !user.user.phone) {
      handleAlert('Пожалуйста, заполните все поля.', 'error');
      return;
    }

    try {
      const updatedUser = await change(user.user.phone, user.user.name, internalName);
      user.setUser(updatedUser);
      handleAlert('Данные успешно обновлены!', 'success');
    } catch (error) {
      console.error('Ошибка при обновлении данных:', error);
      handleAlert('Не удалось обновить данные.', 'error');
    }
  }, [handleAlert, user, internalName]);

  return (
    <Box display={'flex'} justifyContent={'start'}>
      <Card sx={{ m: 2, width: 700 }}>
        <CardContent>
          <Typography variant='h6' sx={{ mb: 2 }}>
            Информация о пользователе
          </Typography>
          <Box display={'flex'} alignItems={'center'} sx={{ mb: 3 }}>
            <Typography
              variant='body1'
              sx={{
                mr: 1,
                fontSize: '12px',
                fontWeight: 'bold',
                color: 'darkgray',
                width: '170px',
              }}
            >
              ID пользователя:
            </Typography>
            <Typography
              variant='body1'
              onClick={handleCopyToClipboard}
              sx={{ cursor: 'pointer', width: '100%', border: 'none' }}
            >
              {user.user?.id || 'Нет ID'}
            </Typography>
          </Box>
          <Box display={'flex'} alignItems={'center'} sx={{ mb: 3 }}>
            <Typography
              variant='body1'
              sx={{
                mr: 1,
                fontSize: '12px',
                fontWeight: 'bold',
                color: 'darkgray',
                width: '170px',
              }}
            >
              Имя пользователя:
            </Typography>
            <TextField
              value={user.user.name || ''}
              onChange={(e) => user.setUser({ name: e.target.value })}
              variant='outlined'
              size='small'
              sx={{ width: '100%' }}
            />
          </Box>
          <Box display={'flex'} alignItems={'center'} sx={{ mb: 3 }}>
            <Typography
              variant='body1'
              sx={{
                mr: 1,
                fontSize: '12px',
                fontWeight: 'bold',
                color: 'darkgray',
                width: '170px',
              }}
            >
              Внутреннее имя:
            </Typography>
            <TextField
              value={internalName}
              onChange={(e) => setInternalName(e.target.value)}
              variant='outlined'
              size='small'
              placeholder='Видно только в панели'
              sx={{ width: '100%' }}
              autoComplete='off'
            />
          </Box>
          {/*<Box display={'flex'} alignItems={'center'} sx={{ mb: 3 }}>*/}
          {/*  <Typography*/}
          {/*    variant='body1'*/}
          {/*    sx={{*/}
          {/*      mr: 1,*/}
          {/*      fontSize: '12px',*/}
          {/*      fontWeight: 'bold',*/}
          {/*      color: 'darkgray',*/}
          {/*      width: '170px',*/}
          {/*    }}*/}
          {/*  >*/}
          {/*    Телефон:*/}
          {/*  </Typography>*/}
          {/*  <TextField*/}
          {/*    value={user.user.phone || ''}*/}
          {/*    onChange={(e) => user.setUser({ phone: e.target.value })}*/}
          {/*    variant='outlined'*/}
          {/*    size='small'*/}
          {/*    sx={{ width: '100%' }}*/}
          {/*  />*/}
          {/*</Box>*/}
          <Box sx={{ mb: 3 }}>
            <Button
              variant='contained'
              color='primary'
              onClick={handleSaveChanges}
              sx={{ borderRadius: '15px' }}
            >
              Сохранить
            </Button>
          </Box>
        </CardContent>
      </Card>

      <Card sx={{ m: 2, mb: 'auto' }}>
        <CardContent>
          <Typography variant='h6' sx={{ mb: 2 }}>
            Смена пароля
          </Typography>
          <Box display={'flex'} alignItems={'center'} sx={{ mb: 3 }}>
            <Typography
              variant='body1'
              sx={{
                mr: 1,
                fontSize: '12px',
                fontWeight: 'bold',
                color: 'darkgray',
                width: '170px',
              }}
            >
              Текущий пароль:
            </Typography>
            <TextField
              type={showCurrentPassword ? 'text' : 'password'}
              variant='outlined'
              size='small'
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position='end'>
                    <IconButton
                      edge='end'
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    >
                      <EyeIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ width: '100%' }}
            />
          </Box>
          <Box display={'flex'} sx={{ mb: 3 }}>
            <Typography
              variant='body1'
              sx={{
                mr: 1,
                fontSize: '12px',
                fontWeight: 'bold',
                color: 'darkgray',
                width: '170px',
              }}
            >
              Новый пароль:
            </Typography>
            <TextField
              type={showNewPassword ? 'text' : 'password'}
              variant='outlined'
              size='small'
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position='end'>
                    <IconButton edge='end' onClick={() => setShowNewPassword(!showNewPassword)}>
                      <EyeIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ width: '100%' }}
            />
          </Box>
          <Button
            variant='contained'
            color='primary'
            onClick={handleChangePassword}
            sx={{ borderRadius: '15px' }}
          >
            Сменить пароль
          </Button>
        </CardContent>
      </Card>
      {alertOpen && (
        <Alert
          severity={alertSeverity}
          onClose={handleAlertClose}
          sx={{
            position: 'fixed',
            top: '128px',
            left: '50%',
            transform: 'translate(-50%, 0)',
            zIndex: 10000,
          }}
        >
          {alertMessage}
        </Alert>
      )}
    </Box>
  );
});

export default Profile;
